import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Popover,
  type PopoverProps,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import type { NotificationFilter, NotificationItem } from '../types';
import { NotificationListItem } from './NotificationListItem';

type PopoverPlacement = 'bottom-end' | 'right-end';

const POPOVER_ORIGINS: Record<
  PopoverPlacement,
  Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin'> & { paperClassName: string }
> = {
  // Below the bell, right edges aligned (top headers)
  'bottom-end': {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
    paperClassName: 'mt-2',
  },
  // To the right of the bell, bottom edges aligned; ml-8 clears the sidebar's px-6 padding (sidebar footer)
  'right-end': {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
    paperClassName: 'ml-8',
  },
};

interface NotificationBellProps {
  /** Where the desktop popover opens relative to the bell */
  popoverPlacement?: PopoverPlacement;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  popoverPlacement = 'bottom-end',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const {
    unreadCount,
    hasCriticalUnread,
    latest,
    latestFilter,
    latestLoading,
    latestLoaded,
    loadLatest,
    setLatestOpen,
    markRead,
    markAllRead,
  } = useNotifications();
  const open = Boolean(anchorEl);
  const origins = POPOVER_ORIGINS[popoverPlacement];
  const badgeContent = useMemo(() => (unreadCount >= 100 ? '99+' : unreadCount), [unreadCount]);

  useEffect(() => {
    setLatestOpen(open);
    return () => setLatestOpen(false);
  }, [open, setLatestOpen]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (_event: React.SyntheticEvent, value: NotificationFilter) => {
    void loadLatest(value);
  };

  const handleItemClick = (item: NotificationItem) => {
    void markRead(item.publicId).finally(() => {
      handleClose();
      navigate(`/notifications/${item.publicId}`);
    });
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  const content = (
    <Box className="flex max-h-[80vh] w-full flex-col bg-white dark:bg-slate-900 md:w-[380px]">
      <Box className="flex items-center justify-between gap-3 px-4 py-3">
        <Typography variant="subtitle1" className="font-semibold text-gray-900 dark:text-white">
          {t('notifications.title')}
        </Typography>
        <Button
          size="small"
          onClick={() => void markAllRead()}
          disabled={unreadCount === 0}
          className="normal-case"
        >
          {t('notifications.markAllRead')}
        </Button>
      </Box>
      <Divider />
      <Tabs
        value={latestFilter}
        onChange={handleFilterChange}
        variant="fullWidth"
        aria-label={t('notifications.tabs.label')}
      >
        <Tab value="all" label={t('notifications.tabs.all')} />
        <Tab value="unread" label={t('notifications.tabs.unread')} />
      </Tabs>
      <Divider />
      <Box className="min-h-[220px] flex-1 overflow-y-auto">
        {latestLoading && !latestLoaded ? (
          <Box className="space-y-3 px-4 py-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <Box key={index} className="flex gap-3">
                <Skeleton variant="rounded" width={36} height={36} />
                <Box className="flex-1">
                  <Skeleton width="80%" />
                  <Skeleton width="100%" />
                  <Skeleton width="45%" />
                </Box>
              </Box>
            ))}
          </Box>
        ) : latest.length === 0 ? (
          <Box className="flex min-h-[220px] items-center justify-center px-6 text-center">
            <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
              {latestFilter === 'unread'
                ? t('notifications.emptyUnread')
                : t('notifications.empty')}
            </Typography>
          </Box>
        ) : (
          latest.map((item) => (
            <NotificationListItem key={item.publicId} item={item} onClick={handleItemClick} />
          ))
        )}
      </Box>
      <Divider />
      <Button onClick={handleViewAll} className="rounded-none py-3 normal-case">
        {t('notifications.viewAll')}
      </Button>
    </Box>
  );

  return (
    <>
      <IconButton
        type="button"
        onClick={handleOpen}
        aria-label={t('notifications.open')}
        className="text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
        sx={{ width: 44, height: 44 }}
      >
        <Badge color="error" badgeContent={badgeContent} invisible={unreadCount === 0}>
          <Bell
            size={21}
            className={hasCriticalUnread ? 'text-amber-500 dark:text-amber-300' : undefined}
          />
        </Badge>
      </IconButton>
      {isSmall ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{ className: 'w-full rounded-t-xl bg-white dark:bg-slate-900' }}
        >
          {content}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={origins.anchorOrigin}
          transformOrigin={origins.transformOrigin}
          PaperProps={{
            className: `${origins.paperClassName} overflow-hidden rounded-lg dark:bg-slate-900`,
          }}
        >
          {content}
        </Popover>
      )}
    </>
  );
};
